import { setCurrentDate, updateFilter, setLayoutSetting } from '../features/calendar/calendar-slice';
import { CombinedFilterData, FilterState, FilterType } from '../helpers/common-types';
import { filterTypes } from '../helpers/filter-helpers';
import { AppDispatch } from './store';

type QueryStringState = {
    currentDate?: string,
    currentAppliedFilters?: FilterState,
    layout?: string
}

const getFilterValues = (filterType: FilterType, urlParams: URLSearchParams) => {
    return urlParams.get(filterType.toLowerCase())
        ?.split(",")
        .map(val => decodeURIComponent(val.trim())) || [""];
}

export const getStateFromQueryString = () => {
    const url = window.location.search;
    const urlParams = new URLSearchParams(url);

    const filterSettings: FilterState = {
        [FilterType.LOCATION]: {
            value: getFilterValues(FilterType.LOCATION, urlParams)
        },
        [FilterType.CATEGORIES]: {
            value: getFilterValues(FilterType.CATEGORIES, urlParams)
        },
        [FilterType.NAME]: {
            value: getFilterValues(FilterType.NAME, urlParams)
        }
    }

    return {
        ...(urlParams.has('date') && {
            currentDate: decodeURIComponent(urlParams.get('date') || '')
        }),
        ...(urlParams.has('layout') && {
            layout: decodeURIComponent(urlParams.get('layout') || '')
        }),
        currentAppliedFilters: filterSettings
    } as QueryStringState;
}

export const writeStateToUrl = (currentDate?: string, currentAppliedFilters?: FilterState, layoutSetting?: string) => {
    const baseUrl = window.location.origin + window.location.pathname;
    let queryParams: string[] = [];

    // Add date parameter if it exists
    if (currentDate) {
        queryParams.push(`date=${encodeURIComponent(currentDate)}`);
    }

    // Add layout parameter if it exists
    if (layoutSetting) {
        queryParams.push(`layout=${encodeURIComponent(layoutSetting)}`);
    }

    // Add filter parameters if they exist
    if (currentAppliedFilters) {
        filterTypes.forEach(filterType => {
            const filterValue = currentAppliedFilters[filterType].value;
            if (filterValue.join() !== "") {
                const encodedValues = filterValue
                    .map(val => encodeURIComponent(val))
                    .join(",");
                queryParams.push(`${filterType.toLowerCase()}=${encodedValues}`);
            }
        });
    }

    return queryParams.length > 0
        ? `${baseUrl}?${queryParams.join("&")}`
        : baseUrl;
}

export const applyUrlParamsToState = (
    dispatch: AppDispatch,
    combinedFilterData: CombinedFilterData,
    applyLayoutSetting: (layout: string) => void) => {
    const urlParams = new URLSearchParams(window.location.search);

    // Update filters based on URL
    filterTypes.forEach(filterType => {
        const filterValue = urlParams.get(filterType.toLowerCase());
        if (filterValue) {
            const filterOptions = combinedFilterData[filterType] || null;
            if (!filterOptions) return;

            const matchingOption = filterOptions.filter(option =>
                option.value?.toLowerCase() === filterValue?.toLowerCase()
            );

            if (matchingOption?.[0]) {
                dispatch(updateFilter({
                    filterType,
                    newState: {
                        value: [matchingOption[0].value],
                        ...(!!matchingOption[0]?.firstOccurance && {
                            firstOccurance: matchingOption[0].firstOccurance
                        })
                    }
                }));
            }
        }
    });

    // Update date if present in URL
    const dateParam = urlParams.get('date');
    if (dateParam) {
        dispatch(setCurrentDate({
            newDate: dateParam,
            shouldChangeRange: true
        }));
    }

    // Update layout if present in URL
    const layoutParam = urlParams.get('layout');
    if (layoutParam) {
        dispatch(setLayoutSetting(layoutParam))
    }
};